import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { Box, CircularProgress, Zoom } from "@mui/material";

const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 200,
    backgroundColor: "#d32f2f",
    color: "#fff",
    fontSize: "0.875rem",
    fontWeight: 500,
    boxShadow: "0px 2px 8px rgba(211, 47, 47, 0.3)",
    padding: "8px 16px",
    borderRadius: "4px",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#d32f2f",
  },
});

/**
 * AccessDeniedTooltip - A wrapper component that shows "Access Denied" tooltip
 * when user clicks on an element without permission
 *
 * @param {boolean} hasPermission - Whether user has permission to perform the action
 * @param {function} onClick - Optional. Pass when THIS wrapper owns the action
 *   (buttons, icons, readOnly checkboxes toggled in onClick). Do NOT pass onClick
 *   when the child owns the action (Switch/Checkbox/input with its own onChange) —
 *   wrapper onClick calls preventDefault and will block those controls.
 * @param {boolean} loading - When true, shows a spinner and blocks interaction
 *   (use while the related API call is in flight).
 * @param {React.ReactNode} children - The element to wrap
 * @param {string} message - Optional custom message (defaults to "Access Denied")
 * @param {string} placement - Tooltip placement (defaults to "top")
 */
const AccessDeniedTooltip = ({
  hasPermission,
  onClick,
  loading = false,
  children,
  message = "Access Denied",
  placement = "top",
  sx = {},
}) => {
  const [open, setOpen] = useState(false);

  const handleClick = (e) => {
    if (loading) {
      e?.stopPropagation?.();
      e?.preventDefault?.();
      return;
    }

    // Always stop bubbling so nested controls (e.g. delete in a row) don't
    // trigger the parent row/card click — including when access is denied.
    e?.stopPropagation?.();

    if (hasPermission) {
      // Wrapper onClick = this component owns the action (buttons, etc.)
      // → preventDefault, then run onClick.
      // No onClick = child owns the action (Switch/checkbox/Link onChange)
      // → do not preventDefault so the control can work.
      if (onClick) {
        e?.preventDefault?.();
        onClick(e);
      }
    } else {
      // Block default (switch toggle, link nav, submit) and show tooltip
      e?.preventDefault?.();
      setOpen(true);
      // Auto-hide after 1.5 seconds
      setTimeout(() => {
        setOpen(false);
      }, 1500);
    }
  };

  const handleMouseDown = (e) => {
    e?.stopPropagation?.();
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <StyledTooltip
      title={message}
      arrow
      placement={placement}
      open={loading ? false : open}
      onClose={handleClose}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      slots={{
        transition: Zoom,
      }}
    >
      <Box
        component="span"
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        sx={{
          cursor: loading ? "default" : "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: loading ? 42 : undefined,
          minHeight: loading ? 24 : undefined,
          ...sx,
        }}
        aria-busy={loading || undefined}
      >
        {loading ? <CircularProgress size={20} thickness={5} /> : children}
      </Box>
    </StyledTooltip>
  );
};

export default AccessDeniedTooltip;
